import { httpsCallable } from 'firebase/functions'
import type { BackendModule, ReadCallback } from 'i18next'

// Track in-progress Firebase loads to prevent duplicate calls for the same language/namespace
const loadingPromises = new Map<string, Promise<Record<string, unknown> | null>>()

/** Cache expiration in ms - 7 days. Used when Chained Backend stores to localStorage. */
export const TRANSLATION_CACHE_EXPIRATION_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Deep merge two objects: base (local) and override (backend)
 * Backend values override local values, but missing keys in backend are kept from local
 */
function deepMerge(base: Record<string, unknown>, override: Record<string, unknown>): Record<string, unknown> {
  const result = { ...base }
  const TRANSLATE_MARKER = '$PLEASE_TRANSLATE$'

  for (const key in override) {
    if (Object.prototype.hasOwnProperty.call(override, key)) {
      const overrideValue = override[key]
      const baseValue = base[key]

      if (
        typeof overrideValue === 'object' &&
        overrideValue !== null &&
        !Array.isArray(overrideValue) &&
        typeof baseValue === 'object' &&
        baseValue !== null &&
        !Array.isArray(baseValue)
      ) {
        // Recursively merge nested objects
        result[key] = deepMerge(baseValue as Record<string, unknown>, overrideValue as Record<string, unknown>)
      } else {
        // Override with backend value (or use backend value if key doesn't exist in base)
        // IMPORTANT: Do NOT let backend wipe out local strings with empty/placeholder values.
        if (typeof overrideValue === 'string') {
          const trimmed = overrideValue.trim()

          // Treat empty string as "missing" (keep local if available)
          if (trimmed === '' && baseValue !== undefined) {
            result[key] = baseValue
            continue
          }

          // Treat "$PLEASE_TRANSLATE$" marker-only strings as "missing" (keep local if available)
          if (overrideValue.startsWith(TRANSLATE_MARKER)) {
            const stripped = overrideValue.substring(TRANSLATE_MARKER.length).trim()
            if (stripped === '' && baseValue !== undefined) {
              result[key] = baseValue
              continue
            }
          }
        }

        result[key] = overrideValue
      }
    }
  }

  return result
}

/**
 * Load local translation file
 */
async function loadLocal(language: string, namespace: string): Promise<Record<string, unknown> | null> {
  try {
    const loadPath = `/locales/${language}/${namespace}.json`
    const response = await fetch(loadPath, { cache: 'no-cache' })

    if (!response.ok) {
      return null
    }

    const data = await response.json()
    return data && typeof data === 'object' ? data : null
  } catch (error) {
    return null
  }
}

/**
 * Load Firebase translations with retry logic
 * Includes deduplication to prevent multiple concurrent calls for the same language/namespace
 */
async function loadFirebase(language: string, namespace: string, retries = 2): Promise<Record<string, unknown> | null> {
  const loadKey = `${language}:${namespace}`

  // Check if a load is already in progress for this combination
  const existingPromise = loadingPromises.get(loadKey)
  if (existingPromise) {
    return existingPromise
  }

  // Create a new load promise
  const loadPromise = (async () => {
    try {
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          // Dynamically get functions to ensure Firebase is initialized
          const { functions } = await import('../../libs/Firebase')

          if (!functions) {
            return null
          }

          // Check if offline (basic detection)
          if (typeof navigator !== 'undefined' && !navigator.onLine) {
            console.warn(`[FirebaseBackend] Offline detected, skipping Firebase load for ${language}/${namespace}`)
            return null
          }

          // Call the getTranslations Cloud Function
          const getTranslationsFn = httpsCallable(functions, 'getTranslations')
          const requestParams = namespace === 'translation' || !namespace ? { language } : { language, namespace }

          const result = await getTranslationsFn(requestParams)
          const response = result.data as { success: boolean; translations: Record<string, unknown> | null }

          if (response.success && response.translations) {
            // Backend returns { [namespace]: bundle } when namespace is specified (including 'translation').
            // Unwrap so we get the namespace bundle for merging with localData (same shape: e.g. { landing: ... }).
            if (namespace && response.translations[namespace]) {
              const namespaceData = response.translations[namespace]
              return typeof namespaceData === 'object' && namespaceData !== null && !Array.isArray(namespaceData)
                ? (namespaceData as Record<string, unknown>)
                : null
            }
            // No namespace or backend returned full document (no namespace key)
            return response.translations
          }

          return null
        } catch (error: unknown) {
          // Check if it's a network error (retry) vs other error (don't retry)
          const isNetworkError =
            (error as Record<string, unknown>)?.code === 'unavailable' ||
            (error as Record<string, unknown>)?.code === 'deadline-exceeded' ||
            String((error as Record<string, unknown>)?.message).includes('network') ||
            String((error as Record<string, unknown>)?.message).includes('fetch')

          if (isNetworkError && attempt < retries) {
            // Exponential backoff: 100ms, 200ms
            const delay = 100 * Math.pow(2, attempt)
            await new Promise((resolve) => setTimeout(resolve, delay))
            continue
          }

          // Log error with context
          if (attempt === retries) {
            console.warn(
              `[FirebaseBackend] Failed to load translations from Firebase for ${language}/${namespace} after ${attempt + 1} attempts:`,
              error
            )
          }

          return null
        }
      }

      return null
    } finally {
      // Always remove from loading map when done (success or failure)
      loadingPromises.delete(loadKey)
    }
  })()

  // Store the promise so concurrent calls can reuse it
  loadingPromises.set(loadKey, loadPromise)

  return loadPromise
}

/**
 * Custom i18next backend that fetches translations from Firebase via Cloud Function.
 * Designed to work as the fallback in Chained Backend (after LocalStorageBackend).
 *
 * When invoked (LocalStorage cache miss or expired):
 * 1. Load local JSON
 * 2. Load Firebase (Cloud Function)
 * 3. Deep-merge (Firebase overrides local)
 * 4. Return merged data in one callback so Chained Backend can cache it in localStorage
 */
const FirebaseBackend: BackendModule = {
  type: 'backend',
  init: () => {
    // No initialization needed
  },
  read: async (language: string, namespace: string, callback: ReadCallback) => {
    const localData = await loadLocal(language, namespace)
    const firebaseData = await loadFirebase(language, namespace)

    // Merge: Firebase overrides local; local fills gaps
    if (firebaseData && typeof firebaseData === 'object' && Object.keys(firebaseData).length > 0) {
      const merged =
        localData && typeof localData === 'object' && Object.keys(localData).length > 0
          ? deepMerge(localData, firebaseData)
          : firebaseData
      callback(null, merged)
      return
    }

    // Firebase failed or empty - use local if available
    if (localData && typeof localData === 'object' && Object.keys(localData).length > 0) {
      callback(null, localData)
      return
    }

    callback(null, null)
  }
}

export default FirebaseBackend
